import React, { useEffect, useState } from "react";
import { Auction } from "../../../models/auction";
import {
  LegendComponent,
  lightenColor,
  ReMixChart,
} from "../../../components/recharts/ReMixCharts";
import {
  FormatAuctionDaily,
  FormatAuctionDailyComparison,
  FormatAuctionMonthly,
  FormatAuctionMonthlyComparison,
} from "./FormatData";
import {
  COLOR_CODES,
  ColorBlue,
  ColorGray,
  formatDMY,
  PrimaryColor,
  SecondaryColor,
  TertiaryColor,
} from "../../../common";
import { SearchBox } from "../../../components/Search";
import { Pagination } from "../../../components/Pagination";
import Loading from "../../../components/Loading";
import { BrushStart } from "../../../models/chart_model";
import { COLORS } from "../../../components/recharts/ReCharts";
import { ExchangeColors } from "../Exchange3/FormatData";
import { get } from "https";
import { FilterTab } from "../../../components/Filters";
import Dropdown from "../../../components/Dropdown";
import { toSafeFloat } from "../../../extensions/number";
import { ChartTable } from "../../../components/table/SummaryTable";
import { useLegendFilter } from "../../../hooks/useLegendFilter";
import Tooltip from "../../../components/tooltip";

interface RAProps {
  forecastData: Auction[];
  startDate: Date;
  endDate: Date;
  loading: boolean;
}

export const RA: React.FC<RAProps> = ({
  forecastData,
  startDate,
  endDate,
  loading,
}) => {
  const [isByChart, setIsByChart] = useState<boolean>(true);
  const [isByMonthlyChart, setIsByMonthlyChart] = useState<boolean>(false);
  const [isComparative, setIsComparative] = useState<boolean>(true);
  const [selectedEntity, setSelectedEntity] = useState<string[]>([]);
  const [selectedLegends, setSelectedLegends] = useState<string[]>([]);
  const [selectExc, setSelectExc] = useState<string[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [selectedAuctioNumber, setSelectedAuctioNumber] = useState<string[]>(
    []
  );
  const [selectedAuctionData, setSelectedAuctionData] = useState<string[]>([]);
  const EXCHANGE_COLOR_MAP: { [key: string]: string } = {
    DEEP: ExchangeColors[1],
    IEX: COLOR_CODES.iex,
    HPX: COLOR_CODES.hpx,
    PXIL: COLOR_CODES.pixl,
  };

  const ITEMS_PER_PAGE = 100;

  const handleSelectType = (type: string) => {
    if (EXCHANGE_TYPES.length === selectExc.length) {
      setSelectExc([type]);
      return;
    }
    if (selectExc.includes(type) && selectExc.length === 1) {
      setSelectExc(EXCHANGE_TYPES);
      return;
    }
    setSelectExc((prev) =>
      prev.includes(type)
        ? prev.filter((item) => item !== type)
        : [...prev, type]
    );
    setCurrentPage(1);
  };

  const handleSelectAuction = (auctionNumber: string) => {
    setSelectedAuctioNumber((prev) =>
      prev.includes(auctionNumber)
        ? prev.filter((item) => item !== auctionNumber)
        : [...prev, auctionNumber]
    );
  };

  const filteredAuctions = (
    filterByEntity = true,
    filterByAuctionNumber = true,
    filterByAuctionDate = true
  ): Auction[] => {
    let res = forecastData;
    if (selectExc.length > 0) {
      res = res.filter((auction) =>
        selectExc.includes(auction.exchange_type__name!)
      );
    }
    if (selectedEntity.length > 0 && filterByEntity) {
      res = res.filter((auction) =>
        selectedEntity.includes(auction.buyer__name!)
      );
    }
    if (selectedAuctioNumber.length > 0 && filterByAuctionNumber) {
      res = res.filter((auction) =>
        selectedAuctioNumber.includes(auction.auction_no!)
      );
    }
    if (selectedAuctionData.length > 0 && filterByAuctionDate) {
      res = res.filter((auction) =>
        selectedAuctionData.includes(auction.auction_date!)
      );
    }
    return res;
  };

  const getActiveExc = () => {
    return selectExc.length > 0 ? selectExc : EXCHANGE_TYPES;
  };

  // const paginatedAuctions = filteredAuctions().slice(
  //   (currentPage - 1) * ITEMS_PER_PAGE,
  //   currentPage * ITEMS_PER_PAGE
  // );

  const legendsForTable: any[] = isByMonthlyChart
    ? [
      ...getActiveExc().map((type, index) => ({
        dataKey: `total_mu_${type.toLowerCase()}`,
        name: `${type}`,
        stroke: ExchangeColors[index + 1],
      })),
      ...getActiveExc().map((type, index) => ({
        dataKey: `weighted_avg_rs_per_kwh_${type.toLowerCase()}`,
        name: `${type} Price`,
        stroke: EXCHANGE_COLOR_MAP[type],
        yAxisId: "right",
      })),
    ]
    : [
      {
        dataKey: "buy_total_quantity_mw",
        name: "Buy Quantity(MW)",
        stroke: COLOR_CODES.secondary,
      },
      {
        dataKey: "allocated_quantity_mw",
        name: "Allocated Quantity(MW)",
        stroke: COLOR_CODES.primary,
      },
      {
        dataKey: "accepted_price_kwh",
        name: "Allocated Price(kWh)",
        stroke: COLOR_CODES.secondary,
        yAxisId: "right",
      },
    ];

  // ▶ Legend filter hook — syncs chart legend clicks with table columns
  const { visibleLegends, handleLegendSelect, selectedLegends: filteredSelectedLegends } =
    useLegendFilter(legendsForTable);

  const tablePar = {
    legends: visibleLegends,
    data: isByMonthlyChart
      ? (FormatAuctionDailyComparison({
        auctions: filteredAuctions(),
        start_date: startDate.toString(),
        end_date: endDate.toString(),
      }) as any)
      : filteredAuctions(),
    tableData: isByMonthlyChart
      ? (FormatAuctionDailyComparison({
        auctions: filteredAuctions(),
        start_date: startDate.toString(),
        end_date: endDate.toString(),
      }) as any)
      : filteredAuctions(),
    itemsPerPage: 100,
    unit: isByMonthlyChart ? "MUs" : "MW",
    extraKeys: isByMonthlyChart
      ? ["date"]
      : [
        "auction_no",
        "auction_date",
        "delivery_start_date",
        "delivery_start_time",
        "delivery_end_date",
        "delivery_end_time",
        "buyer__name",
        "exchange_type__name",
      ],
    extraLabels: isByMonthlyChart
      ? ["Date"]
      : [
        "Auction No",
        "Auction Date",
        "Start Date",
        "Start Time",
        "End Date",
        "End Time",
        "Buyer",
        "Exchange",
      ],
    filename: "Reverse Auction Data",
    showTotal: false,
  };

  const aa = filteredAuctions(true, true, false)
    .map((auction) => ({
      label: formatDMY(auction.auction_date!),
      value: auction.auction_date!,
    }))
    .filter((v, i, a) => a.findIndex((t) => t.value === v.value) === i)
    .sort((a, b) => new Date(b.value).getTime() - new Date(a.value).getTime());

  console.log("Filtered Auctions: ", aa);

  return (
    <div>
      {loading && <Loading />}

      <div className="filterSection m-5">
        <div className="flex space-x-4 w-full">
          <FilterTab
            options={EXCHANGE_TYPES}
            isRounded={true}
            selected={selectExc}
            isSecondary={true}
            onClick={(type: string) => handleSelectType(type)}
          />

          {isByChart && (
            <Dropdown
              options={["Cumulative", "Comparative"]}
              selected={isComparative ? "Comparative" : "Cumulative"}
              onSelect={(type: string) =>
                setIsComparative(type === "Comparative")
              }
            />
          )}
          <div className="search-box">
            <SearchBox
              options={filteredAuctions(false, false, false)
                .map((auction) => ({
                  label: auction.buyer__name!,
                  value: auction.buyer__name!,
                }))
                .filter(
                  (v, i, a) => a.findIndex((t) => t.value === v.value) === i
                )}
              isMany={true}
              selectedOptions={selectedEntity.map((entity) => ({
                label: entity,
                value: entity,
              }))}
              onChange={(e: any) => {
                setSelectedEntity(e.map((item: any) => item.value));
                setCurrentPage(1);
              }}
              placeholder="Select Entity"
            />
          </div>
          <div className="search-box">
            <SearchBox
              options={filteredAuctions(false, false, false)
                .map((auction) => ({
                  label: formatDMY(auction.auction_date!),
                  value: auction.auction_date!,
                }))
                .filter(
                  (v, i, a) => a.findIndex((t) => t.value === v.value) === i
                )
                .sort(
                  (a, b) =>
                    new Date(b.value).getTime() - new Date(a.value).getTime()
                )}
              isMany={true}
              selectedOptions={selectedAuctionData.map((auctionData) => ({
                label: auctionData,
                value: auctionData,
              }))}
              onChange={(e: any) =>
                setSelectedAuctionData(e.map((item: any) => item.value))
              }
              placeholder="Auction Date"
            />
          </div>
          <div className="auctionnum-search-box">
            <SearchBox
              options={filteredAuctions(false, false, false)
                .map((auction) => ({
                  label: auction.auction_no!,
                  value: auction.auction_no!,
                }))
                .filter(
                  (v, i, a) => a.findIndex((t) => t.value === v.value) === i
                )}
              isMany={true}
              selectedOptions={selectedAuctioNumber.map((auctionNumber) => ({
                label: auctionNumber,
                value: auctionNumber,
              }))}
              onChange={(e: any) => {
                setSelectedAuctioNumber(e.map((item: any) => item.value));
                setCurrentPage(1);
              }}
              placeholder="Select Auction No."
            />
          </div>
        </div>
        {isByChart && (
          <FilterTab
            options={["Daily", "Monthly"]}
            isRounded={true}
            selected={isByMonthlyChart ? ["Monthly"] : ["Daily"]}
            isSecondary={true}
            onClick={(type: string) => setIsByMonthlyChart(type === "Monthly")}
          />
        )}
        <FilterTab
          options={["Chart", "Table"]}
          isRounded={true}
          selected={isByChart ? ["Chart"] : ["Table"]}
          isSecondary={false}
          onClick={(type: string) => setIsByChart(type === "Chart")}
        />
      </div>
      <div className="detContainermain container-radius m-5">
        {isByChart ? (
          <div className="raChart container-radius">
            <div className="flex justify-center">
              {isComparative && (
                <div className="flex gap-x-8">
                  {LegendComponent(
                    {
                      dataKey: "total_mu",
                      stroke: COLOR_CODES.secondary,
                      name: "Volume(MUs)",
                      type: 1,
                      legendColor: COLOR_CODES.secondary,
                    },
                    0,
                    [],
                    () => { }
                  )}
                  {LegendComponent(
                    {
                      dataKey: "weighted_avg_rs_per_kwh",
                      stroke: COLOR_CODES.secondary,
                      name: "Wt. Avg Price(Rs/KWh)",
                      yAxisId: "right",
                    },
                    1,
                    [],
                    () => { }
                  )}
                </div>
              )}
            </div>
            {!isComparative ? (
              <>
                <ReMixChart
                  onlyTitle={true}
                  data={
                    isByMonthlyChart
                      ? FormatAuctionMonthly({
                        start_date: formatDateString(startDate),
                        end_date: formatDateString(endDate),
                        auctions: filteredAuctions(),
                      })
                      : (FormatAuctionDaily({
                        auctions: filteredAuctions(),
                        start_date: startDate.toString(),
                        end_date: endDate.toString(),
                      }) as any)
                  }
                  xDataKey="date"
                  legends={[
                    {
                      dataKey: "total_mu",
                      stroke: "#E0E0E0",
                      name: "Allocated MU",
                      type: 1,
                      stackId: "a",
                      legendColor: "rgb(159, 159, 159)",
                    },
                    {
                      dataKey: "unallocated_mu",
                      stroke: "gray",
                      name: "Unallocated MU",
                      type: 1,
                      stackId: "a",
                      legendColor: "gray",
                    },
                    {
                      dataKey: "weighted_avg_rs_per_kwh",
                      stroke: PrimaryColor,
                      name: "Wt. Avg Price(Rs/KWh)",
                      yAxisId: "right",
                    },
                  ]}
                  xaxisHeight={50}
                  unit="MUs"
                  secondYAxisLabel="Weighted Average Price(Rs/KWh)"
                  yAxisLabel="Total MUs"
                  chartContainerHeight="calc(100vh - 200px)"
                />
                {/* <ReMixChart
                data={forecastData.map((auction) => ({
                  accepted_price_kwh: auction.accepted_price_kwh,
                  deleivery_date: auction.delivery_start_date,
                  
                  buyer: auction.buyer__name,
                  allocated_quantity_mw: auction.allocated_quantity_mw,
                  auction_no: auction.auction_no,
                  unallocated_mu: auction.buy_total_quantity_mw! - auction.allocated_quantity_mw!,


                })) as any}
                legends  = {[
                  {
                    dataKey: "allocated_quantity_mw",
                    stroke: PrimaryColor,
                    name: "Allocated MU",
                    type: 1,
                    stackId: "a",
                    legendColor: "rgb(159, 159, 159)",
                  },
                  {
                    dataKey: "unallocated_mu",
                    stroke: "gray",
                    name: "Unallocated MU",
                    type: 1,
                    stackId: "a",
                    legendColor: "gray",
                  },
                  {
                    dataKey: "accepted_price_kwh",
                    stroke: PrimaryColor,
                    name: "Wt. Avg Price(Rs/KWh)",
                    yAxisId: "right",
                  },
                ]}
                xDataKey="auction_no"

                /> */}
              </>
            ) : (
              <ReMixChart
                onlyTitle={true}
                data={
                  isByMonthlyChart
                    ? FormatAuctionMonthlyComparison({
                      start_date: formatDateString(startDate),
                      end_date: formatDateString(endDate),
                      auctions: filteredAuctions(),
                    })
                    : (FormatAuctionDailyComparison({
                      auctions: filteredAuctions(),
                      start_date: startDate.toString(),
                      end_date: endDate.toString(),
                    }) as any) ?? []
                }
                xDataKey="date"
                legendBreakIndex={
                  getActiveExc().length * 2 - 1 < 2
                    ? undefined
                    : getActiveExc().length * 2 - 1
                }
                xaxisHeight={50}
                legends={[
                  ...getActiveExc()
                    .map((type, index) =>
                      [
                        {
                          dataKey: `total_mu_${type.toLowerCase()}`,
                          stroke: lightenColor(EXCHANGE_COLOR_MAP[type], 40),
                          name: `${type} Allocated`,
                          stackId: type,
                          type: 1,
                        },
                        {
                          dataKey: `unallocated_mu_${type.toLowerCase()}`,
                          stroke: lightenColor(EXCHANGE_COLOR_MAP[type], 10),
                          name: `${type} Unallocated`,
                          stackId: type,
                          type: 1,
                        },
                      ].flat()
                    )
                    .flat(),
                  // {} as any,
                  ...getActiveExc()
                    .map(
                      (type, index) =>
                        [
                          {
                            dataKey: `weighted_avg_rs_per_kwh_${type.toLowerCase()}`,
                            stroke: EXCHANGE_COLOR_MAP[type],
                            name: `${type} Price`,
                            yAxisId: "right",
                          },
                        ].flat()
                      // { dataKey: `total_mu_${type.toLowerCase()}`, stroke: ExchangeColors[index + 1], name: `${type} Total MU`, type: 1 }
                    )
                    .flat(),
                ]}
                brushIndex={{
                  startIndex: 0,
                  endIndex:
                    (
                      FormatAuctionDailyComparison({
                        auctions: filteredAuctions(),
                        start_date: startDate.toString(),
                        end_date: endDate.toString(),
                      }) as any
                    ).length - 1,
                }}
                unit="MUs"
                secondYAxisLabel="Weighted Average Price (Rs/KWh)"
                yAxisLabel="Total MUs"
                chartContainerHeight="calc(100vh - 200px)"
                showBrush={
                  !isByMonthlyChart &&
                  (
                    FormatAuctionDailyComparison({
                      auctions: filteredAuctions(),
                      start_date: startDate.toString(),
                      end_date: endDate.toString(),
                    }) as any
                  ).length > 10
                }
              />
            )}
          </div>
        ) : (
          <ChartTable {...(tablePar as any)} />
        )}
        <div className="gna-footer-container">
          <div className="grid grid-cols-3 justify-between gap-5 ">
            <Tooltip note={<p>Reverse Auction</p>} text=" ">
              <p className="gna-footer-text">Source</p>
            </Tooltip>
            <p className="gna-footer-text col-span-1 text-center">
              Powered by <strong>GNA Energy</strong>
            </p>
            <strong className="gna-footer-text col-span-1 text-end">
              Beta Version
            </strong>
          </div>
        </div>
      </div>
    </div>
  );
};

const EXCHANGE_TYPES = ["IEX", "HPX", "PXIL", "DEEP"];

const formatDateString = (date: Date): string =>
  date.toLocaleDateString("en-GB").split("/").reverse().join("-");
